// [목적] 폴리오 카드용 좋아요 칩 — ExplorePage/HomePage 목록 카드에서 재사용하는 소형 좋아요 토글
// [이유] fe-folio-social-wire — ExFolioCard/RecommendedFolioCard 의 정적 ♡ 표시를 실 API 로 배선.
//         카드 전체가 ButtonBase(클릭 시 폴리오 상세로 navigate)이므로 이 칩은 stopPropagation 으로
//         독립 클릭 영역을 확보한다.
// [사이드 이팩트] useFolioLike 캐시(queryKeys.folioSocial.likeState) 공유 — 동일 folioId 카드가 여러 곳에
//                 동시 노출돼도(예: ExplorePage 추천 섹션 + 전체 그리드) 좋아요 상태가 함께 갱신된다.
// [수정 시 고려사항] role="button"+tabIndex 로 키보드 Enter/Space 접근 보장(카드 내부 중첩 버튼 회피 위해
//                   ButtonBase 대신 span 사용 — DOM 상 button-in-button 방지).

import { Box } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { Folio } from '@gangwoncanvas/types';

import { useAuthGate } from '../../hooks/useAuthGate.js';
import { useFolioLike } from '../../hooks/useFolioLike.js';
import { LoginPromptModal } from '../auth/LoginPromptModal.js';

const c = tokens.color;
const L = 'light' as const;
const INK_LOW = c.ink.low[L];
const DANGER = c.danger[500][L];

/**
 * [목적] 좋아요 아이콘 + 카운트 인터랙티브 칩
 * [이유] fe-folio-social-wire — 카드 목록에서 좋아요 토글(useFolioLike) + 게스트 가드(useAuthGate)
 * [사이드 이팩트] 클릭 시 부모 카드의 onClick(상세 이동)로 이벤트가 버블링되지 않도록 stopPropagation
 * [수정 시 고려사항] folio.likeCount 는 최초 마운트 시 1회만 캐시 시드로 사용(useFolioLike 제약과 동일)
 */
export function FolioLikeChip({ folio }: { folio: Folio }): JSX.Element {
  // [G2-1, 2026-09-17] folio.viewer?.liked 전달 — 목록 응답엔 계약 C1 viewer 가 없어(BE 단건 전용) 대부분
  // undefined 로 기존 동작(liked=false 시작)과 동일하지만, 상세 페이지에서 넘어온 folio 객체 등
  // viewer 가 채워진 경우를 대비해 방어적으로 전달한다.
  const { liked, likeCount, toggle, isPending } = useFolioLike(
    folio.id,
    folio.likeCount,
    folio.viewer?.liked,
  );
  const { guard, isPromptOpen, closePrompt, pendingIntent } = useAuthGate('folio.like');

  function handleActivate(e: { stopPropagation: () => void; preventDefault?: () => void }): void {
    e.preventDefault?.();
    e.stopPropagation();
    guard(toggle);
  }

  return (
    <>
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-pressed={liked}
        aria-label={liked ? '좋아요 취소' : '좋아요'}
        onClick={handleActivate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleActivate(e);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '3px',
          cursor: 'pointer',
          color: liked ? DANGER : INK_LOW,
          opacity: isPending ? 0.7 : 1,
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ fontSize: 11 }}>
          {liked ? '♥' : '♡'}
        </Box>
        {likeCount}
      </Box>
      <LoginPromptModal open={isPromptOpen} intent={pendingIntent} onClose={closePrompt} />
    </>
  );
}
